/**
 * TimeOfDayColorGrader — v4 Filters color grading for the day/night cycle
 * (DON-600).
 *
 * Uses Phaser 4's `ColorMatrix` filter controller on the main camera to apply
 * time-of-day tinting:
 *   - Dawn:  warm pink/gold wash
 *   - Day:   neutral / full brightness
 *   - Dusk:  amber/rust saturated
 *   - Night: deep blue/navy, desaturated and dark
 *
 * A Vignette filter is layered on top for analog edge-darkening feel (§4.10).
 * Both filters can be disabled via `setEnabled(false)` for low-end devices
 * without affecting game logic (§guardrail 6).
 *
 * All transitions are driven by `TimeSystem.EVENTS.TICK` — no frame-count
 * coupling (§1.8). Uses WebGL filters only; no-ops gracefully on Canvas
 * (§1.2 — do NOT use Phaser v3 postFX APIs).
 *
 * Phaser 4 filter API:
 *   camera.filters.external.addColorMatrix() → Phaser.Filters.ColorMatrix
 *   cmFilter.colorMatrix → Phaser.Display.ColorMatrix  (the actual matrix ops)
 *   camera.filters.external.remove(filter)
 *
 * Usage:
 *   // In scene create():
 *   this.colorGrader = new TimeOfDayColorGrader(this);
 *   this.colorGrader.attachToCamera(this.cameras.main);
 *   this.events.on(TimeSystem.EVENTS.TICK, this.colorGrader.onTimeTick, this.colorGrader);
 *
 *   // In scene shutdown():
 *   this.events.off(TimeSystem.EVENTS.TICK, this.colorGrader.onTimeTick, this.colorGrader);
 *   this.colorGrader.destroy();
 */

import * as Phaser from 'phaser';
import {
  type TimeOfDay,
  type AmbientLight,
  getPhaseProgress,
} from '@game/shared';
import type { TimeTickPayload } from '../systems/TimeSystem';
import { gradeAt, vignetteAt, VIGNETTE_STRENGTH } from './timeOfDayGrade';

// ── Color grade presets ────────────────────────────────────────────────────
//
// The presets and the shape of the night live in ./timeOfDayGrade, which has no
// Phaser in it and so can be tested. This file owns the camera filters; that
// one owns the only interesting question, which is what colour the hour is.

/** Additive realm-biome color overlay (AGE-1018). Applied on top of
 *  the time-of-day grade during biome transitions. */
export interface RealmColorOverlay {
  hueRotate: number;
  saturation: number;
  brightness: number;
  contrast: number;
}

// ── Public class ──────────────────────────────────────────────────────────────

export class TimeOfDayColorGrader {
  private scene: Phaser.Scene;
  private camera: Phaser.Cameras.Scene2D.Camera | null = null;

  /** Phaser 4 ColorMatrix filter controller. Null when WebGL unavailable. */
  private cmFilter: Phaser.Filters.ColorMatrix | null = null;
  /** Phaser 4 Vignette filter for analog edge-darkening. Null when WebGL unavailable. */
  private vignetteFilter: Phaser.Filters.Vignette | null = null;

  private enabled: boolean = true;

  /** AGE-1018: additive realm-biome color overlay, blended on top of
   *  the time-of-day grade during biome boundary crossings. */
  private realmOverlay: RealmColorOverlay | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Setup ─────────────────────────────────────────────────────────────────

  /**
   * Attaches color grading to the given camera.
   * Safe to call on Canvas renderers — filters are skipped gracefully.
   */
  attachToCamera(camera: Phaser.Cameras.Scene2D.Camera): void {
    this.camera = camera;

    if (!this.isWebGL()) return;

    const fl = camera.filters;
    if (!fl) return;

    try {
      // External = post-camera-render; applies in world/camera space (§1.2)
      this.cmFilter       = fl.external.addColorMatrix();
      this.vignetteFilter = fl.external.addVignette(0.5, 0.5, 0.8, VIGNETTE_STRENGTH.day);
    } catch {
      this.cmFilter       = null;
      this.vignetteFilter = null;
    }

    // Apply the neutral day preset immediately so the first frame is correct
    this.applyGrade('day', 0);
  }

  // ── TimeSystem hook ────────────────────────────────────────────────────────

  /** Register as the `TimeSystem.EVENTS.TICK` handler. */
  onTimeTick(payload: TimeTickPayload): void {
    if (!this.enabled) return;
    const progress = getPhaseProgress(payload.fraction);
    this.applyGrade(payload.phase, progress);
    this.updateVignette(payload.phase, progress);
    this.updateCameraBackground(payload.ambient);
  }

  // ── Controls ──────────────────────────────────────────────────────────────

  /**
   * AGE-1018: Set (or clear) the realm-biome color overlay. Values are
   * *additive* to the time-of-day grade and take effect on the next tick.
   */
  setRealmOverlay(overlay: RealmColorOverlay | null): void {
    this.realmOverlay = overlay;
  }

  /** Disable/enable the filter stack. When disabled, resets to neutral. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.cmFilter?.colorMatrix.reset();
      if (this.vignetteFilter) {
        this.vignetteFilter.strength = 0;
      }
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  destroy(): void {
    const fl = this.camera?.filters;
    if (fl) {
      try {
        if (this.cmFilter)       fl.external.remove(this.cmFilter);
        if (this.vignetteFilter) fl.external.remove(this.vignetteFilter);
      } catch {
        // Already disposed by Phaser
      }
    }
    this.cmFilter       = null;
    this.vignetteFilter = null;
    this.camera         = null;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private isWebGL(): boolean {
    return this.scene.renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer;
  }

  /**
   * Interpolates the color grade between the current phase and the next,
   * then writes it to the `ColorMatrix` filter.
   * `progress` is 0..1 through the current phase window.
   */
  private applyGrade(phase: TimeOfDay, progress: number): void {
    if (!this.cmFilter) return;

    const g = gradeAt(phase, progress);
    let hue        = g.hueRotate;
    let saturation = g.saturation;
    let brightness = g.brightness;
    let contrast   = g.contrast;

    // AGE-1018: layer realm-biome color overlay additively.
    if (this.realmOverlay) {
      hue        += this.realmOverlay.hueRotate;
      saturation += this.realmOverlay.saturation;
      brightness += this.realmOverlay.brightness;
      contrast   += this.realmOverlay.contrast;
    }

    // Access the Display.ColorMatrix via the filter controller's .colorMatrix
    // Every ColorMatrix op takes a `multiply` flag that defaults to FALSE, and
    // `multiply(a, false)` calls reset() before applying. Calling these four in
    // sequence without it therefore did not compose them — each one wiped the
    // last, so only the final non-neutral term survived and the hue and
    // saturation of the grade were silently discarded. Pass `true` so they
    // chain onto the reset matrix below.
    const cm = this.cmFilter.colorMatrix;
    cm.reset();
    if (hue        !== 0)  cm.hue(hue, true);
    if (saturation !== 0)  cm.saturate(saturation, true);
    if (brightness !== 1)  cm.brightness(brightness, true);
    if (contrast   !== 0)  cm.contrast(contrast, true);
  }

  /** Blends vignette strength along the same arc the colour follows. */
  private updateVignette(phase: TimeOfDay, progress: number): void {
    if (!this.vignetteFilter) return;
    this.vignetteFilter.strength = vignetteAt(phase, progress);
  }

  /**
   * Sets the camera's background colour to the current ambient hue.
   * Affects transparent areas and letterboxing for a subtle sky tint.
   */
  private updateCameraBackground(ambient: AmbientLight): void {
    if (!this.camera) return;
    const hex = (ambient.r << 16) | (ambient.g << 8) | ambient.b;
    this.camera.setBackgroundColor(hex);
  }
}
